import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["expanded", "defaultExpanded", "onChange"];
import React, { forwardRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import ControlledAccordion from './ControlledAccordion';
import AccordionItem from './AccordionItem';
import AccordionHeader from './AccordionHeader';
import AccordionPanel from './AccordionPanel';
/**
 * An accordion is a vertically stacked set of interactive headings that each contain a title
 * representing a section of content. The headings function as controls that enable users to reveal
 * or hide their associated sections of content. Accordions are commonly used to reduce the need
 * to scroll when presenting multiple sections of content on a single page.
 *
 * @since 8.43.0
 */

var Accordion = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var controlledExpanded = _ref.expanded,
      _ref$defaultExpanded = _ref.defaultExpanded,
      defaultExpanded = _ref$defaultExpanded === void 0 ? [] : _ref$defaultExpanded,
      onChange = _ref.onChange,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(defaultExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      uncontrolledExpanded = _useState2[0],
      setUncontrolledExpanded = _useState2[1];

  var onChangeCallback = useCallback(function (newValue, nextState) {
    setUncontrolledExpanded(nextState);

    if (typeof onChange === 'function') {
      onChange(newValue, nextState);
    }
  }, [onChange, setUncontrolledExpanded]);
  return /*#__PURE__*/React.createElement(ControlledAccordion, _extends({
    ref: forwardedRef,
    expanded: typeof controlledExpanded !== 'undefined' ? controlledExpanded : uncontrolledExpanded,
    onChange: onChangeCallback
  }, rest));
});
Accordion.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,

  /**
   * A default value for the expanded panel's index, or indices, in an uncontrolled accordion
   * component when it is initially rendered.
   */
  defaultExpanded: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),

  /**
   * The index, or array of indices, for expanded accordion panels. The `expanded` prop should be
   * used along with `onChange` prop to create controlled accordion component.
   */
  expanded: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),

  /**
   * The callback for when accordion state has changed.
   *
   * This function will be passed the selected panel's `index` and the `nextState` as index or
   * array of indices.
   */
  onChange: PropTypes.func,

  /**
   * The callback for when accordion focus has changed.
   *
   * This function will be passed the focused panel's `index`.
   */
  onPanelFocus: PropTypes.func
} : {};
Accordion.displayName = 'Accordion';
Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Panel = AccordionPanel;
export default Accordion;